import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Download, Loader2, ArrowLeft, ClipboardList, Send, Check } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GdprConsent from "@/components/GdprConsent";
import { COMPANY, BUILDING_TYPES, api } from "@/lib/api";
import { downloadHtmlPdf, htmlToPdfBase64, orderHtml } from "@/lib/pdf";

const MATERIALS = ["Fasádny polystyrén EPS 70F", "Grafitový EPS (šedý)", "Minerálna vata (fasádna)", "XPS (extrudovaný polystyrén)"];
const PLASTERS = ["Silikónová omietka", "Silikón-silikátová omietka", "Silikátová omietka", "Akrylátová omietka", "Minerálna omietka"];
const TYPE_LABEL = { family_house: "Rodinný dom", apartment_building: "Bytový dom", building: "Budova / objekt" };

export default function Objednavka() {
  const [params] = useSearchParams();
  const fromViz = params.get("src") === "vizualizacia";
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [o, setO] = useState({
    name: "", customer_address: "", email: "", phone: "",
    object_address: "", object_type: "", area: "", material: "",
    thickness: "", plaster: "", term: "", note: "", price: "", price_per_m2: "",
  });

  useEffect(() => {
    document.title = params.get("src") === "vizualizacia" ? "Cenová ponuka zateplenia — Zateplenia.eu" : "Objednávka zateplenia — Zateplenia.eu";
    window.scrollTo(0, 0);
    setO((prev) => ({
      ...prev,
      name: params.get("name") || prev.name,
      email: params.get("email") || prev.email,
      area: params.get("area") || prev.area,
      material: params.get("material") || prev.material,
      thickness: params.get("thickness") || prev.thickness,
      object_type: TYPE_LABEL[params.get("type")] ? params.get("type") : prev.object_type,
      price: params.get("price") || prev.price,
      price_per_m2: params.get("ppm") || prev.price_per_m2,
      note: params.get("note") || prev.note,
    }));
  }, [params]);

  const set = (k, v) => setO((s) => ({ ...s, [k]: v }));

  const download = async () => {
    if (!o.name.trim()) { toast.error("Vyplňte aspoň meno objednávateľa."); return; }
    setBusy(true);
    try {
      const payload = { ...o, object_type: TYPE_LABEL[o.object_type] || o.object_type };
      await downloadHtmlPdf(orderHtml(payload, COMPANY), "objednavka-zateplenie.pdf");
      toast.success("Objednávka stiahnutá ako PDF.");
    } catch {
      toast.error("Nepodarilo sa vytvoriť PDF.");
    } finally { setBusy(false); }
  };

  const sendOrder = async () => {
    if (!o.name.trim()) { toast.error("Vyplňte aspoň meno objednávateľa."); return; }
    if (!o.email.trim() && !o.phone.trim()) { toast.error("Zadajte e-mail alebo telefón, aby sme vás mohli kontaktovať."); return; }
    if (!gdpr) { toast.error("Potvrďte súhlas so spracovaním osobných údajov."); return; }
    setSending(true);
    try {
      const payload = { ...o, object_type: TYPE_LABEL[o.object_type] || o.object_type };
      let pdf_base64 = "";
      try { pdf_base64 = await htmlToPdfBase64(orderHtml(payload, COMPANY)); } catch { /* PDF nepovinné */ }
      await api.post("/insulation-order", { ...payload, pdf_base64 });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Odoslanie zlyhalo. Skúste to znova.");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-ink min-h-screen" data-testid="objednavka-page">
      <Header />
      <section className="max-w-[900px] mx-auto px-5 md:px-10 pt-32 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-8 font-mono2 uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Späť na hlavnú
        </Link>

        <div className="flex items-center gap-2 text-gold mb-4">
          <ClipboardList className="w-5 h-5" />
          <p className="font-mono2 uppercase tracking-[0.3em] text-xs">{fromViz ? "Cenová ponuka" : "Objednávka"}</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-[0.98] text-white mb-3" data-testid="obj-heading">{fromViz ? "Nezáväzná cenová ponuka" : "Záväzná objednávka zateplenia"}</h1>
        <p className="text-white/55 text-sm md:text-base mb-10 max-w-2xl">
          {fromViz
            ? "Údaje z vašej AI vizualizácie sme predvyplnili — doplňte kontakt a odošlite. Pripravíme vám nezáväznú cenovú ponuku; konečná cena a termín budú potvrdené po obhliadke."
            : "Vyplňte údaje a objednávku nám rovno odošlite, alebo si ju stiahnite ako PDF na vytlačenie a podpis. Konečná cena a termín budú potvrdené po obhliadke."}
        </p>

        {o.price && !sent && (
          <div className="mb-10 border border-gold/40 bg-gold/5 p-6 flex flex-wrap items-end justify-between gap-4" data-testid="obj-price-box">
            <div>
              <div className="text-white/50 text-xs font-mono2 uppercase tracking-widest mb-1">{fromViz ? "Orientačná cena z vizualizácie (s DPH)" : "Orientačná cena z kalkulačky (s DPH)"}</div>
              <div className="font-display text-5xl text-gold leading-none">{Number(o.price).toLocaleString("sk-SK")} €</div>
              {o.price_per_m2 && <div className="text-white/50 text-sm mt-2">≈ {Number(o.price_per_m2).toLocaleString("sk-SK")} €/m² · {o.area} m²</div>}
            </div>
            <p className="text-white/40 text-xs max-w-xs">Ide o orientačnú cenu z online výpočtu. Presnú cenu potvrdíme po bezplatnej obhliadke.</p>
          </div>
        )}

        {sent ? (
          <div className="border border-gold/30 bg-ink-2 p-10 text-center max-w-xl" data-testid="obj-success">
            <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-gold" />
            </div>
            <h2 className="font-display text-3xl text-white">{fromViz ? "Žiadosť o ponuku sme prijali!" : "Objednávku sme prijali!"}</h2>
            <p className="text-white/60 mt-4 leading-relaxed">{fromViz ? "Ďakujeme. Čoskoro vás kontaktujeme s nezáväznou cenovou ponukou." : "Ďakujeme. Čoskoro vás kontaktujeme a dohodneme bezplatnú obhliadku."}</p>
            <Link to="/" className="inline-block mt-8 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors">
              Späť na hlavnú stránku
            </Link>
          </div>
        ) : (
        <div className="space-y-8">
          <div>
            <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Objednávateľ</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Meno a priezvisko / firma *" v={o.name} on={(v) => set("name", v)} testid="obj-name" />
              <F label="Adresa" v={o.customer_address} on={(v) => set("customer_address", v)} testid="obj-cust-address" />
              <F label="E-mail" type="email" v={o.email} on={(v) => set("email", v)} testid="obj-email" />
              <F label="Telefón" v={o.phone} on={(v) => set("phone", v)} testid="obj-phone" />
            </div>
          </div>

          <div>
            <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Predmet objednávky</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Adresa objektu" v={o.object_address} on={(v) => set("object_address", v)} testid="obj-object-address" />
              <S label="Typ objektu" v={o.object_type} on={(v) => set("object_type", v)} testid="obj-type"
                 options={BUILDING_TYPES.map((b) => [b.key, b.label])} />
              <F label="Plocha fasády (m²)" v={o.area} on={(v) => set("area", v)} testid="obj-area" />
              <S label="Materiál" v={o.material} on={(v) => set("material", v)} testid="obj-material"
                 options={MATERIALS.map((m) => [m, m])} />
              <F label="Hrúbka izolácie (mm)" v={o.thickness} on={(v) => set("thickness", v)} testid="obj-thickness" />
              <S label="Povrchová úprava (omietka)" v={o.plaster} on={(v) => set("plaster", v)} testid="obj-plaster"
                 options={PLASTERS.map((p) => [p, p])} />
              <F label="Požadovaný termín" v={o.term} on={(v) => set("term", v)} testid="obj-term" />
            </div>
            <div className="mt-4">
              <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">Poznámka</label>
              <textarea value={o.note} onChange={(e) => set("note", e.target.value)} rows={3} data-testid="obj-note"
                className="mt-1 w-full bg-ink-2 border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none resize-none" />
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 text-white/50 text-sm">
            <span className="text-white/70 font-semibold">Zhotoviteľ:</span> {COMPANY.name}, {COMPANY.street}, {COMPANY.city} · IČO: {COMPANY.ico}
          </div>

          <GdprConsent checked={gdpr} onChange={setGdpr} testid="obj-gdpr" />

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={sendOrder} disabled={sending} data-testid="obj-send"
              className="inline-flex items-center justify-center gap-2 bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors disabled:opacity-50">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {fromViz ? "Odoslať žiadosť o ponuku" : "Odoslať objednávku"}
            </button>
            <button onClick={download} disabled={busy} data-testid="obj-download-pdf"
              className="inline-flex items-center justify-center gap-2 border border-gold/50 text-gold px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Stiahnuť ako PDF
            </button>
          </div>
        </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function F({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="mt-1 w-full bg-ink-2 border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none" />
    </div>
  );
}

function S({ label, v, on, options, testid }) {
  return (
    <div>
      <label className="text-white/50 text-xs font-mono2 uppercase tracking-widest">{label}</label>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="mt-1 w-full bg-ink-2 border border-white/15 px-4 py-3 text-white text-sm focus:border-gold outline-none">
        <option value="" className="bg-ink">— vyberte —</option>
        {options.map(([val, lab]) => <option key={val} value={val} className="bg-ink">{lab}</option>)}
      </select>
    </div>
  );
}
